/**
 * CMS SDK - useContents Hook
 * 内容列表管理 Hook
 */

import { useState, useCallback, useEffect } from 'react';
import { useCMS } from '../context/CMSContext';

/**
 * 内容列表 Hook
 * @param {Object} initialFilters - 初始筛选条件
 * @returns {Object} 内容列表操作对象
 */
export function useContents(initialFilters = {}) {
  const { api } = useCMS();
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0,
  });

  /**
   * 加载内容列表
   */
  const load = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getContents({
        page: pagination.page,
        page_size: pagination.pageSize,
        ...filters,
        ...params,
      });
      const items = result.items || result.data || [];
      setContents(items);
      setPagination(prev => ({
        ...prev,
        total: result.total || items.length,
        totalPages: result.total_pages || Math.ceil((result.total || items.length) / prev.pageSize),
      }));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api, filters, pagination.page, pagination.pageSize]);

  /**
   * 刷新列表
   */
  const refresh = useCallback(() => {
    return load();
  }, [load]);

  /**
   * 搜索内容
   */
  const search = useCallback(async (keyword) => {
    setFilters(prev => ({ ...prev, keyword }));
    setPagination(prev => ({ ...prev, page: 1 }));
    return load({ keyword, page: 1 });
  }, [load]);

  /**
   * 按状态筛选
   */
  const filterByStatus = useCallback((status) => {
    setFilters(prev => ({ ...prev, status }));
    setPagination(prev => ({ ...prev, page: 1 }));
    return load({ status, page: 1 });
  }, [load]);

  /**
   * 按分类筛选
   */
  const filterByCategory = useCallback((categoryId) => {
    setFilters(prev => ({ ...prev, category_id: categoryId }));
    setPagination(prev => ({ ...prev, page: 1 }));
    return load({ category_id: categoryId, page: 1 });
  }, [load]);

  /**
   * 按标签筛选
   */
  const filterByTag = useCallback((tagId) => {
    setFilters(prev => ({ ...prev, tag_id: tagId }));
    setPagination(prev => ({ ...prev, page: 1 }));
    return load({ tag_id: tagId, page: 1 });
  }, [load]);

  /**
   * 排序
   */
  const sortBy = useCallback((field, order = 'desc') => {
    setFilters(prev => ({ ...prev, sort_by: field, sort_order: order }));
    return load({ sort_by: field, sort_order: order });
  }, [load]);

  /**
   * 设置页码
   */
  const setPage = useCallback((page) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  /**
   * 设置每页数量
   */
  const setPageSize = useCallback((pageSize) => {
    setPagination(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  /**
   * 清除筛选
   */
  const clearFilters = useCallback(() => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
    return load({});
  }, [load]);

  /**
   * 批量删除
   */
  const deleteMultiple = useCallback(async (ids) => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all(ids.map(id => api.deleteContent(id)));
      setContents(prev => prev.filter(c => !ids.includes(c.id)));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  // 当分页或筛选条件变化时自动重新加载
  useEffect(() => {
    load();
  }, [pagination.page, pagination.pageSize]);

  return {
    contents,
    loading,
    error,
    filters,
    pagination,
    load,
    refresh,
    search,
    filterByStatus,
    filterByCategory,
    filterByTag,
    sortBy,
    setPage,
    setPageSize,
    setFilters,
    clearFilters,
    deleteMultiple,
  };
}

export default useContents;
